// Web-specific media upload helper
// Note: The backend CORS needs to be configured to return the specific origin
// instead of '*' when credentials are included

import type { MediaFile } from '@common/media/types';

const API_BASE_URL = 'https://api.familypal.com/familypal/v1';

function normalizeMediaFile(f: any): MediaFile {
  return {
    id: f.id,
    type: f.type || '',
    uri: f.uri || '',
    bucket: f.bucket || '',
    key: f.key || '',
    name: f.name || '',
    size: typeof f.size === 'string' ? parseInt(f.size, 10) : (f.size || 0),
    mimeType: f.mime_type || f.mimeType || '',
    thumbnail: f.thumbnail || '',
    thumbnailUri: f.thumbnail_uri || f.thumbnailUri || '',
    width: f.width || 0,
    height: f.height || 0,
    miniPreview: f.mini_preview || f.miniPreview || '',
    hasPreview: f.has_preview ?? f.hasPreview ?? false,
  };
}

export async function uploadMediaFileWeb(
  file: { uri: string; name: string; mimeType: string; size: number },
  dataId: string,
  onProgress?: (progress: number) => void,
): Promise<MediaFile> {
  // Determine file type from MIME type
  let fileType = 'FILE_TYPE_FILE';
  if (file.mimeType?.startsWith('image/')) {
    fileType = 'FILE_TYPE_IMAGE';
  } else if (file.mimeType?.startsWith('video/')) {
    fileType = 'FILE_TYPE_VIDEO';
  } else if (file.mimeType?.startsWith('audio/')) {
    fileType = 'FILE_TYPE_AUDIO';
  }

  // Step 1: Get presigned URL (with credentials for auth)
  const uploadUrlResponse = await fetch(`${API_BASE_URL}/media/files/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      filename: file.name,
      type: fileType,
      mime_type: file.mimeType,
      file_size: file.size,
    }),
  });

  if (!uploadUrlResponse.ok) {
    const errorText = await uploadUrlResponse.text();
    throw new Error(`Failed to get upload URL: ${uploadUrlResponse.status} ${errorText}`);
  }

  const uploadInfo = await uploadUrlResponse.json();
  const presignedUrl = uploadInfo.upload_url || uploadInfo.uploadUrl;
  const fileId = uploadInfo.file_id || uploadInfo.fileId;

  onProgress?.(10);

  // Step 2: Upload to presigned URL (no credentials needed for S3)
  const blob = await fetch(file.uri).then((r) => r.blob());
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.mimeType || 'application/octet-stream',
    },
    body: blob,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`);
  }

  onProgress?.(80);

  // Step 3: Register the upload (with credentials for auth)
  const registerResponse = await fetch(`${API_BASE_URL}/media/files/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      file_id: fileId,
      filename: file.name,
      type: fileType,
      mime_type: file.mimeType,
      file_size: file.size,
      data_id: dataId,
    }),
  });

  if (!registerResponse.ok) {
    const errorText = await registerResponse.text();
    throw new Error(`Failed to register upload: ${registerResponse.status} ${errorText}`);
  }

  const registered = await registerResponse.json();

  onProgress?.(100);

  return normalizeMediaFile(registered);
}
